import type { ClientItem } from 'owa-mail-store';
import type Item from 'owa-service/lib/contract/Item';
import { DecryptionState } from 'owa-mail-store/lib/store/schema/DecryptionState';
import { isAccountFeatureEnabled } from 'owa-feature-flags';

/**
 * Check if add-ins should be disabled during or after decryption for the given item.
 * Add-ins should be disabled when:
 * 1. Decryption is in progress (InProgress state)
 * 2. Decryption is completed AND there is a decrypted body available
 *
 * This prevents add-ins from being launched while decryption is happening
 * or after decryption has completed with content.
 *
 * @param item - The item to check (will be cast to ClientItem internally)
 * @returns true if add-ins should be disabled, false otherwise
 */
export default function shouldDisableAddinsForDecryption(item: Item): boolean {
    // Cast to ClientItem to access AddinData
    const clientItem = item as ClientItem;

    // Check if the addin-decryption feature is enabled
    if (!isAccountFeatureEnabled('addin-decryption', clientItem.MailboxInfo)) {
        return false;
    }

    const decryptionState = clientItem.AddinData?.DecryptionState;
    const hasDecryptedBody = !!clientItem.AddinData?.Body;

    // Disable add-ins if decryption is in progress
    if (decryptionState === DecryptionState.InProgress) {
        return true;
    }

    // Disable add-ins if decryption is completed AND there's a decrypted body
    if (decryptionState === DecryptionState.Completed && hasDecryptedBody) {
        return true;
    }

    return false;
}
